import {
  ActivationItemApi,
  AudienceApiResponse,
  AUDIENCE_TYPES,
  LOOKALIKE_METHODS,
  LookalikeMethod
} from '../../../services/audience-api.service';
import { AudienceCreateFormValue, AudienceFormStatus, AudienceQuality } from '../create/audience-create.component';
import { AudienceDestination, AudienceItem } from './audience.types';
import {
  extractCreatorFallback,
  formatDate,
  formatSchedule,
  formatVolume,
  isForeverEndDate,
  mapScheduleToForm,
  normalizeFrontendPreview,
  normalizePublicFlag,
  normalizeStatus,
  normalizeTagsValue,
  parseChannelData,
  toInputDate,
  todayIsoDate,
  toNumericValue
} from './audience-format';
import {
  extractLocationFromFilters,
  extractSociodemoFromFilters,
  mapFilterTypeToRuleGroup,
  normalizeFilterItems,
  normalizePublisherEntities,
  ProviderLookup,
  rebuildRuleBlocksFromFilters
} from './audience-filters';
import {
  mapPreviewAccuracyToForm,
  normalizePreviewAccuracy,
  resolvePreviewStepFromQualityIndex
} from './audience-preview';

// Mapping API → AudienceItem (lista/dettaglio) e AudienceItem → valore del form (edit/clone).
// Estratto da AudienceComponent: funzioni pure, le lookup del componente arrivano come parametri.

export const TNCID_PLUS_GROUP_ID = '5f6efd54-1488-11f0-9621-42010a9c0008';

export interface DestinationLookups {
  destinationNames: ReadonlyMap<string, string>;
  destinationTypeInfo: ReadonlyMap<string, { typeId: number; typeName: string }>;
}

export function mapActivationDestination(item: ActivationItemApi, lookups: DestinationLookups): AudienceDestination {
  const destinationId = item.DestinationID ? String(item.DestinationID) : null;
  const name = destinationId ? (lookups.destinationNames.get(destinationId) ?? `Destination ${destinationId}`) : `Destination ${item.ID}`;
  const typeInfo = destinationId ? (lookups.destinationTypeInfo.get(destinationId) ?? null) : null;
  return {
    id: item.ID,
    destinationId,
    destinationTypeId: typeInfo?.typeId ?? null,
    destinationTypeName: typeInfo?.typeName ?? '—',
    name,
    externalSegmentId: item.ExternalSegmentID || '—',
    externalSegmentIdRaw: item.ExternalSegmentID ?? null,
    volume: formatVolume(item.Volume ?? null),
    nextPushDate: formatDate(item.NextExecutionDate || item.LastPushTime),
    lastPushDate: formatDate(item.LastPushTime),
    lastPushRaw: item.LastPushTime ?? null,
    nextExecutionRaw: item.NextExecutionDate ?? null,
    entityIdRaw: item.EntityID ?? null,
    status: item.Status || '—',
    channelData: parseChannelData(item.ChannelData),
    excludeControlGroup: !!item.ExcludeControlGroup,
    extendToFamily: !!item.ExtendToFamily,
    description: item.Description ?? null
  };
}

export function mapApiAudience(
  api: AudienceApiResponse,
  lookups: DestinationLookups,
  options?: { ignorePreviewData?: boolean }
): AudienceItem {
  const createdById = api.CreatedByUserID ?? null;
  const fallbackName = extractCreatorFallback(api);
  const entityId = (api as any).EntityID ?? null;
  const entityType = (api as any).EntityType ?? null;
  const activationItems = api.ActivationItems ?? [];
  const filterItems = normalizeFilterItems(api.FilterItems);
  const queryComposition = api.QueryComposition ?? '';
  const extendVolumes = toNumericValue(api.ExtendVolumes);
  const globalTimerange = toNumericValue(api.GlobalTimerange);
  const tags = normalizeTagsValue(api.Tags);
  const expandSourceGroupId = (api as any).ExpandSourceGroupID ?? null;
  const frontendPreview = normalizeFrontendPreview(api.FrontendPreview);

  // Ignora i dati di preview SOLO se esplicitamente richiesto (quando si crea una nuova draft)
  const previewAccuracy = options?.ignorePreviewData
    ? null
    : normalizePreviewAccuracy(api.PreviewAccuracy ?? api.AccuracyPreview ?? null);

  const audienceQuality = (api as any).AudienceQuality ?? null;
  const isPublic = normalizePublicFlag(api.IsPublic);
  return {
    id: api.ID,
    name: api.Name,
    description: api.Description ?? '',
    brief: (api as any).Brief ?? null,
    status: normalizeStatus(api.Status),
    isRunning: !!api.IsRunning,
    volume: formatVolume(api.Volumes),
    volumeRaw: toNumericValue(api.Volumes),
    createdById,
    createdBy: fallbackName,
    createdAt: formatDate(api.CreationTime),
    endDate: formatDate(api.EndDate),
    endDateRaw: api.EndDate ?? null,
    lastExtraction: formatDate(api.LastExtraction),
    lastExtractionRaw: api.LastExtraction ?? null,
    nextExtraction: formatDate(api.NextSchedulationDate),
    nextExtractionRaw: api.NextSchedulationDate ?? null,
    schedule: formatSchedule(api.Schedulation),
    entityId,
    entityType,
    destinations: activationItems.map(item => mapActivationDestination(item, lookups)),
    filterItems,
    queryComposition,
    extendVolumes,
    globalTimerange,
    audienceQuality,
    audienceQualityIndex: toNumericValue((api as any).AudienceQualityIndex),
    tags,
    expandSourceGroupId,
    frontendPreview,
    expandSimilarUsers: !!api.ExpandSimilarUsers,
    sourceGroupIds: Array.isArray(api.SourceGroupIDs) ? api.SourceGroupIDs : null,
    isPublic,
    previewAccuracy
  };
}

export function isCustomQueryAudience(audience: AudienceItem): boolean {
  // Il backend è l'unica fonte di verità per l'AudienceType: se è presente, il frontend non
  // deve reinterpretarlo in base a pattern nei filtri (es. legacy o dati inconsistenti). I
  // controlli sui filtri restano solo come fallback per audience senza FrontendPreview salvato.
  const recordedType = audience.frontendPreview?.AudienceType;
  if (recordedType) {
    return recordedType === AUDIENCE_TYPES.CUSTOM_QUERY;
  }
  if ((audience.filterItems ?? []).some(item => item.filterType === 'Custom')) return true;
  if (audience.queryComposition && /\bCustom#\d+/.test(audience.queryComposition)) return true;
  return false;
}

export function isCustomAudience(audience: AudienceItem): boolean {
  const audienceTypeFromPreview = audience.frontendPreview?.AudienceType ?? null;
  const hasRuleFiltersFromItems = (audience.filterItems ?? []).some(
    item => !!mapFilterTypeToRuleGroup(item.filterType)
  );

  return hasRuleFiltersFromItems || audienceTypeFromPreview === AUDIENCE_TYPES.CUSTOM;
}

/** FrontendPreview.LookalikeMethod salvato → valore del form; assente o sconosciuto = 'knn'. */
export function toLookalikeMethod(value: string | null | undefined): LookalikeMethod {
  return (LOOKALIKE_METHODS as readonly string[]).includes(value ?? '') ? value as LookalikeMethod : 'knn';
}

export interface AudienceFormMappingDeps {
  providers: ProviderLookup | null | undefined;
  resolveCountryName: (countryCode: string) => string;
  /** Data odierna YYYY-MM-DD, usata come startDate quando l'audience non ne ha una. */
  today?: string;
}

export function mapAudienceToFormValue(audience: AudienceItem, deps: AudienceFormMappingDeps): AudienceCreateFormValue {
  const startDate = toInputDate(audience.nextExtractionRaw) || (deps.today ?? todayIsoDate());
  const isForeverDate = isForeverEndDate(audience.endDateRaw);
  const ongoing = !audience.endDateRaw || isForeverDate;
  // Se ongoing=true, endDate deve essere vuoto nel form
  const endDate = ongoing ? '' : toInputDate(audience.endDateRaw);
  const sociodemo = extractSociodemoFromFilters(audience.filterItems);
  const locationInfo = extractLocationFromFilters(audience.filterItems, deps.resolveCountryName);
  // Non popolare aiDescription se brief è vuoto
  const aiDescription = (audience.brief ?? '').toString().trim();
  // Remap publisher entities to their unique resolvable provider before rebuilding rule blocks
  const normalizedFilterItems = normalizePublisherEntities(audience.filterItems ?? [], deps.providers);
  const rebuiltRuleBlocks = rebuildRuleBlocksFromFilters(
    normalizedFilterItems,
    audience.queryComposition
  );

  // Determine whether this is a rule-based (custom) audience.
  // filterItems are the source of truth: if any rule-type filter exists, it's custom regardless
  // of what AudienceType claims (legacy audiences may have a wrong AudienceType saved).
  const audienceTypeFromPreview = audience.frontendPreview?.AudienceType ?? null;
  const hasRuleFiltersFromItems = (audience.filterItems ?? []).some(
    item => !!mapFilterTypeToRuleGroup(item.filterType)
  );
  const hasRuleFilters = hasRuleFiltersFromItems
    || audienceTypeFromPreview === AUDIENCE_TYPES.CUSTOM
    || (!audienceTypeFromPreview && rebuiltRuleBlocks.length > 0);

  // audienceQuality e extendVolumes sono mutualmente esclusivi:
  // se audienceQuality è 'high'|'medium'|'low' → extendVolumes = null, e viceversa
  const rawQuality = audience.audienceQuality?.toString().toLowerCase();
  const isValidQuality = rawQuality === 'high' || rawQuality === 'medium' || rawQuality === 'low';
  const formAudienceQuality: AudienceQuality = isValidQuality ? rawQuality as AudienceQuality : null;
  const formExtendVolumes: number | null = isValidQuality ? null : (audience.extendVolumes ?? null);

  const expandSimilarUsers = !!audience.expandSimilarUsers;

  // Deduce useAllNetworkData from sourceGroupId
  const useAllNetworkData = hasRuleFilters
    ? true
    : audience.expandSourceGroupId === TNCID_PLUS_GROUP_ID;

  const previewAccuracy = audience.previewAccuracy;
  const shouldPopulatePreviewData = !!previewAccuracy && (
    audience.status === 'draft'
    || audience.audienceQualityIndex !== null
    || (expandSimilarUsers && (hasRuleFilters || audienceTypeFromPreview === AUDIENCE_TYPES.INTERESTS))
  );

  return {
    type: isCustomQueryAudience(audience)
      ? AUDIENCE_TYPES.CUSTOM_QUERY
      : hasRuleFilters ? AUDIENCE_TYPES.CUSTOM : AUDIENCE_TYPES.INTERESTS,
    name: audience.name,
    description: audience.description ?? '',
    frequency: mapScheduleToForm(audience.schedule),
    startDate,
    endDate,
    ongoing,
    aiDescription,
    useAllNetworkData,
    expandSimilarUsers,
    expandVersionWeighted: audience.frontendPreview?.ExpandVersion === 'weighted',
    lookalikeMethod: toLookalikeMethod(audience.frontendPreview?.LookalikeMethod),
    ageRange: sociodemo.ageRange,
    gender: sociodemo.gender,
    location: locationInfo.location,
    regions: locationInfo.regions,
    audienceQuality: formAudienceQuality,
    extendVolumes: formExtendVolumes,
    aiTags: audience.tags ?? [],
    ruleBlocks: hasRuleFilters ? rebuiltRuleBlocks : [],
    previewData: shouldPopulatePreviewData
      ? mapPreviewAccuracyToForm(previewAccuracy!, audience.audienceQualityIndex)
      : null,
    fullRangePreviewData: shouldPopulatePreviewData && audience.fullRangePreviewAccuracy
      ? mapPreviewAccuracyToForm(audience.fullRangePreviewAccuracy, audience.audienceQualityIndex)
      : null,
    previewAccuracyStep: shouldPopulatePreviewData
      ? resolvePreviewStepFromQualityIndex(previewAccuracy!, audience.audienceQualityIndex)
      : null,
    audienceStatus: audience.status as AudienceFormStatus,
    isPublic: audience.isPublic === true,
    actualVolume: audience.volumeRaw ?? null,
    audienceId: audience.id
  };
}
